/**
 * RequestPasswordReset Store - State management for RequestPasswordReset.tsx page
 *
 * Related page: frontend/src/pages/RequestPasswordReset.tsx
 *
 * This store demonstrates the "form with validation" pattern:
 * - requestPasswordResetFormAtom: form data (email, isSubmitting)
 * - requestPasswordResetFormErrorsAtom: null before first submit, then contains errors
 * - Once submitted, errors update in real-time as user types
 */

import { atom } from 'jotai'
import { authClient } from '../client'
import { FormError } from 'txom-frontend-libs'

// ============================================================================
// FORM STATE
// ============================================================================

const initialRequestPasswordResetForm = {
  email: '',
  isSubmitting: false,
}

type RequestPasswordResetForm = typeof initialRequestPasswordResetForm

export const requestPasswordResetFormAtom = atom(initialRequestPasswordResetForm)

// Success state - true when email has been sent successfully
export const requestPasswordResetSuccessAtom = atom(false)

// ============================================================================
// FORM ERRORS
// - null: form has never been submitted, don't show errors
// - object: form has been submitted, show errors and validate in real-time
// ============================================================================

type RequestPasswordResetFormErrors = {
  form?: string // General form-level error (e.g., "Failed to send reset email")
  email?: string
}

export const requestPasswordResetFormErrorsAtom = atom<RequestPasswordResetFormErrors | null>(null)

// ============================================================================
// VALIDATION
// ============================================================================

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function hasFieldErrors(errors: RequestPasswordResetFormErrors): boolean {
  const { form: _form, ...fieldErrors } = errors
  return Object.keys(fieldErrors).length > 0
}

const validateRequestPasswordResetFormAtom = atom(null, (get, set) => {
  const form = get(requestPasswordResetFormAtom)
  const errors: RequestPasswordResetFormErrors = {}

  if (!form.email.trim()) {
    errors.email = 'Email is required'
  } else if (!isValidEmail(form.email)) {
    errors.email = 'Invalid email format'
  }

  set(requestPasswordResetFormErrorsAtom, errors)
  return hasFieldErrors(errors)
})

const setRequestPasswordResetFormErrorAtom = atom(null, (_get, set, err: unknown, fallback: string) => {
  const message = err instanceof FormError ? err.message : (err instanceof Error ? err.message || fallback : fallback)
  set(requestPasswordResetFormErrorsAtom, (current) => ({ ...(current ?? {}), form: message }))
})

// ============================================================================
// FORM ACTIONS
// ============================================================================

export const updateRequestPasswordResetFormAtom = atom(null, (get, set, updates: Partial<RequestPasswordResetForm>) => {
  const current = get(requestPasswordResetFormAtom)
  set(requestPasswordResetFormAtom, { ...current, ...updates })

  // If form has been submitted before (errors !== null), validate in real-time
  if (get(requestPasswordResetFormErrorsAtom) !== null) {
    set(validateRequestPasswordResetFormAtom)
  }
})

export const submitRequestPasswordResetAtom = atom(null, async (get, set) => {
  const form = get(requestPasswordResetFormAtom)

  // Validate and enable real-time validation mode
  if (set(validateRequestPasswordResetFormAtom)) {
    throw new FormError('Please fix the errors above')
  }

  set(requestPasswordResetFormAtom, { ...form, isSubmitting: true })

  try {
    const res = await authClient.requestPasswordReset({
      email: form.email,
      redirectTo: window.location.origin + '/reset-password',
    })
    if (res.error) {
      throw new FormError(res.error.message || 'Failed to send reset email')
    }
    set(requestPasswordResetSuccessAtom, true)
  } catch (err) {
    set(setRequestPasswordResetFormErrorAtom, err, 'Failed to send reset email')
    throw err
  } finally {
    set(requestPasswordResetFormAtom, (current) => ({ ...current, isSubmitting: false }))
  }
})

// ============================================================================
// ROUTE LOADERS
// ============================================================================

type Store = ReturnType<typeof import('jotai').createStore>

export async function loader(_store: Store) {
  return null
}
